/**
 * Platform diagnostics page.
 *
 * Opened directly from inside the wrapped app (iOS / Android) to
 * verify that the JavaScript-Native bridge is being injected
 * correctly. Shows every signal the routing layer uses, so you can
 * spot exactly what's missing if payments don't switch to IAP.
 *
 * URL: /app/diagnostica
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Copy, Check, AlertCircle, RefreshCw } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePaymentsConfig, getPaymentPlatform, pickProvider } from '../lib/paymentPlatform';
import GpsBackgroundDiagnostic from '../components/GpsBackgroundDiagnostic';
import RevenueCatPriceDiagnostic from '../components/RevenueCatPriceDiagnostic';
import { BUILD_DATE } from '../buildInfo';

function safeGet(fn) {
  try {
    return fn();
  } catch (e) {
    return `[error: ${e?.message || e}]`;
  }
}

function gatherSignals() {
  const w = typeof window !== 'undefined' ? window : {};
  const nav = typeof navigator !== 'undefined' ? navigator : {};

  // Capacitor 7+ exposes plugin descriptors via `getPluginNames()` AND
  // via the Plugins object map. Read both so the diagnostic page can
  // tell apart "plugin not bundled in the IPA" from "plugin bundled
  // but JS module not loaded".
  let pluginNames = [];
  try {
    if (w.Capacitor?.Plugins) {
      pluginNames = Object.keys(w.Capacitor.Plugins);
    }
  } catch { /* ignore */ }

  return {
    timestamp: new Date().toISOString(),

    // Capacitor bridge (preferred)
    capacitor: {
      present: !!w.Capacitor,
      platform: safeGet(() => w.Capacitor?.getPlatform?.()),
      isNative: safeGet(() => w.Capacitor?.isNativePlatform?.()),
      pluginNames,
      firebaseMessagingPresent: pluginNames.includes('FirebaseMessaging'),
      pushNotificationsPresent: pluginNames.includes('PushNotifications'),
    },

    // Natively wrapper
    natively: {
      present: !!w.natively,
      platform: safeGet(() => w.natively?.platform),
      isWebview: safeGet(() => w.natively?.isWebview),
    },

    // RevenueCat / IAP SDK injected by Natively (only present when
    // the "In-App Purchases" feature is enabled in the Natively
    // dashboard AND a new build has been shipped).
    nativelyPurchases: {
      present: typeof w.NativelyPurchases !== 'undefined',
      isFunction: typeof w.NativelyPurchases === 'function',
    },

    // Firebase Push Notifications SDK injected by Natively (only
    // present when "Push Notifications - Firebase" is enabled on the
    // dashboard AND a new build has been shipped).
    nativelyPush: {
      present: typeof w.NativelyFirebaseNotifications !== 'undefined',
      isFunction: typeof w.NativelyFirebaseNotifications === 'function',
    },

    // Browser identity
    browser: {
      userAgent: nav.userAgent || '',
      platform: nav.platform || '',
      standalone: safeGet(() => w.navigator?.standalone),
      displayMode: safeGet(() =>
        w.matchMedia?.('(display-mode: standalone)')?.matches
      ),
    },

    // Window dimensions (useful for "real device" sanity)
    window: {
      width: w.innerWidth,
      height: w.innerHeight,
      devicePixelRatio: w.devicePixelRatio,
    },
  };
}

export default function Diagnostica() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const { data: paymentsConfig } = usePaymentsConfig();

  // Admin-only page. Non-admin users who somehow find the URL get
  // redirected to their profile rather than seeing internal logs.
  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) {
      navigate('/app/profilo', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const [signals, setSignals] = useState(() => gatherSignals());
  const [copied, setCopied] = useState(false);

  const refresh = () => setSignals(gatherSignals());

  // Natively injects its JS bridges (NativelyPurchases,
  // NativelyFirebaseNotifications, etc.) AFTER the page has loaded.
  // Auto-refresh for the first ~5 seconds so users don't see false
  // negatives when they open this page quickly.
  useEffect(() => {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const fresh = gatherSignals();
      setSignals(fresh);
      const allBridgesLoaded =
        fresh.nativelyPurchases.present && fresh.nativelyPush.present;
      if (allBridgesLoaded || attempts >= 5) {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const detectedPlatform = getPaymentPlatform();
  const provider = pickProvider(detectedPlatform, paymentsConfig?.providers);

  // -------- Interactive push test ----------------------------------
  // Lets us verify end-to-end push delivery WITHOUT plugging the
  // iPhone into Xcode. The button runs the whole sequence
  // (permission → APNs → FCM token → save to backend → trigger test
  // push) and shows every step's outcome inline.
  const [pushLog, setPushLog] = useState([]);
  const [pushRunning, setPushRunning] = useState(false);
  const [pushLogCopied, setPushLogCopied] = useState(false);
  const [lastFcmToken, setLastFcmToken] = useState(null);
  const [fcmTokenCopied, setFcmTokenCopied] = useState(false);
  const appendLog = (step, status, detail = '') => {
    setPushLog((prev) => [
      ...prev,
      { ts: new Date().toLocaleTimeString(), step, status, detail },
    ]);
  };
  const runPushTest = async () => {
    setPushLog([]);
    setPushRunning(true);
    try {
      const { Capacitor } = await import('@capacitor/core');
      const plat = Capacitor.getPlatform();
      appendLog('Capacitor.getPlatform()', 'OK', plat);

      // Check server-side OneSignal config first.
      const sessionToken = localStorage.getItem('session_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      try {
        const r = await fetch(`${apiUrl}/api/notifications/push-status-v2`, {
          headers: {
            ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
          },
          credentials: 'include',
        });
        const j = await r.json().catch(() => ({}));
        const ok = j?.onesignal?.configured && j?.onesignal?.probe_ok;
        appendLog('GET /api/notifications/push-status-v2', ok ? 'OK' : 'FAIL', JSON.stringify(j));
        if (!ok) {
          appendLog('Stop', 'FAIL',
            'Il backend non ha OneSignal configurato. ' +
            'Verifica che ONESIGNAL_APP_ID e ONESIGNAL_REST_API_KEY siano impostati e che la REST Key sia valida.');
          setPushRunning(false);
          return;
        }
      } catch (e) {
        appendLog('GET /api/notifications/push-status-v2', 'FAIL', e?.message || String(e));
      }

      // Init OneSignal + request permission
      let osLib;
      try {
        osLib = await import('../lib/onesignal');
        await osLib.initOneSignal();
        appendLog('initOneSignal()', 'OK', osLib.isNativeOneSignalAvailable() ? 'native plugin' : 'web SDK');
      } catch (e) {
        appendLog('initOneSignal()', 'FAIL', e?.message || String(e));
        setPushRunning(false);
        return;
      }

      let granted;
      try {
        granted = await osLib.requestOneSignalPermission();
        appendLog('requestOneSignalPermission()', granted ? 'OK' : 'FAIL', `granted=${granted}`);
      } catch (e) {
        appendLog('requestOneSignalPermission()', 'FAIL', e?.message || String(e));
        setPushRunning(false);
        return;
      }
      if (!granted) {
        appendLog('Stop', 'FAIL', 'Permesso notifiche negato. Vai nelle impostazioni iPhone → Slimmy → Notifiche.');
        setPushRunning(false);
        return;
      }

      // Poll for player_id
      let playerId = null;
      for (let i = 1; i <= 8; i += 1) {
        playerId = await osLib.getOneSignalPlayerId();
        if (playerId) break;
        appendLog(`getOneSignalPlayerId() tentativo ${i}`, 'WAIT', 'no player_id yet');
        await new Promise((r) => setTimeout(r, 1000));
      }
      if (!playerId) {
        appendLog('getOneSignalPlayerId()', 'FAIL',
          'OneSignal non ha restituito un player_id dopo 8 tentativi. ' +
          'Su iOS: assicurati che APNs sia configurato su OneSignal Dashboard → Settings → Platforms.');
        setPushRunning(false);
        return;
      }
      appendLog('getOneSignalPlayerId()', 'OK', playerId);
      setLastFcmToken(playerId);  // reuse the "copy" button for the player id

      // Save player_id to backend
      try {
        const r = await fetch(`${apiUrl}/api/notifications/onesignal-player`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({ player_id: playerId, platform: plat }),
        });
        const j = await r.json();
        appendLog('POST /api/notifications/onesignal-player', r.ok ? 'OK' : 'FAIL', JSON.stringify(j));
      } catch (e) {
        appendLog('POST /api/notifications/onesignal-player', 'FAIL', e?.message || String(e));
        setPushRunning(false);
        return;
      }

      // Bind external user id if logged in (best-effort)
      try {
        const me = await fetch(`${apiUrl}/api/auth/me`, {
          headers: { ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}) },
          credentials: 'include',
        }).then((r) => r.json()).catch(() => null);
        if (me?.user_id) {
          await osLib.loginOneSignalUser(me.user_id);
          appendLog('loginOneSignalUser()', 'OK', me.user_id);
        }
      } catch { /* non-fatal */ }

      // Trigger SELF test push via OneSignal
      try {
        const r = await fetch(`${apiUrl}/api/notifications/self-test-push`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({}),
        });
        const j = await r.json().catch(() => ({}));
        const ok = j.success === true;
        appendLog(
          'POST /api/notifications/self-test-push',
          ok ? 'OK' : 'FAIL',
          JSON.stringify(j),
        );
      } catch (e) {
        appendLog('POST /api/notifications/self-test-push', 'FAIL', e?.message || String(e));
      }

      appendLog('Fine', 'OK',
        'Se tutti gli step sopra sono OK, chiudi l\'app (swipe up) e dopo 5 secondi dovresti vedere la push. ' +
        'Apri anche OneSignal Dashboard → Delivery per vedere il log lato server.');
    } finally {
      setPushRunning(false);
    }
  };

  // Mini verdict for each layer.
  const capacitorOk =
    signals.capacitor.present && ['ios', 'android'].includes(signals.capacitor.platform);
  const ua = (signals.browser.userAgent || '').toLowerCase();
  // Natively is considered "OK" if EITHER the explicit field is set,
  // OR the UA contains the `Natively/...` signature. This covers
  // builds that don't expose `window.natively.platform`.
  const nativelyUaHit =
    ua.includes('natively/ios') || ua.includes('natively/android');
  const nativelyOk =
    (signals.natively.present || nativelyUaHit) &&
    (['ios', 'android'].includes(signals.natively.platform) || nativelyUaHit);
  const anyBridgeOk = capacitorOk || nativelyOk;

  const payload = JSON.stringify(
    { detectedPlatform, provider, signals, paymentsConfig },
    null,
    2
  );

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older webviews: fall back to alert so the user
      // can long-press → copy manually.
      // eslint-disable-next-line no-alert
      alert(payload);
    }
  };

  return (
    <div
      className={`min-h-screen ${isDark ? 'bg-[#080D1A] text-white' : 'bg-white text-gray-900'}`}
      data-testid="diagnostica-page"
    >
      {/* Header */}
      <div className="sticky top-0 z-20 px-4 py-3 flex items-center gap-3 border-b backdrop-blur-md"
           style={{
             borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
             paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)',
           }}>
        <button
          onClick={() => navigate(-1)}
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isDark ? 'bg-white/10' : 'bg-gray-100'
          }`}
          data-testid="diag-back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-display text-lg flex-1">Diagnostica piattaforma</h1>
        <button
          onClick={refresh}
          className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isDark ? 'bg-white/10' : 'bg-gray-100'
          }`}
          data-testid="diag-refresh-btn"
          aria-label="Aggiorna"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4 pb-32">
        {/* Build info — first value so it's instantly visible. Lets
            the user verify which build is actually running when they
            check the page after a TestFlight update. */}
        <section
          className={`rounded-2xl p-3 flex items-center justify-between ${
            isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'
          }`}
          data-testid="diag-build-info"
        >
          <span className={`text-xs uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            Build
          </span>
          <span className={`text-sm font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {BUILD_DATE}
          </span>
        </section>

        {/* Verdict card -------------------------------------------- */}
        <section
          className={`rounded-2xl p-5 ${
            anyBridgeOk
              ? isDark
                ? 'bg-green-500/10 border border-green-500/40'
                : 'bg-green-50 border border-green-200'
              : isDark
              ? 'bg-amber-500/10 border border-amber-500/40'
              : 'bg-amber-50 border border-amber-200'
          }`}
          data-testid="diag-verdict"
        >
          <div className="flex items-center gap-2 mb-2">
            {anyBridgeOk ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-500" />
            )}
            <h2 className="font-bold text-base">
              {anyBridgeOk ? 'Bridge nativo rilevato ✓' : 'Nessun bridge nativo'}
            </h2>
          </div>
          <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
            Piattaforma rilevata:&nbsp;
            <strong>{detectedPlatform}</strong>
            <br />
            Provider che verrà usato per il pagamento:&nbsp;
            <strong>{provider}</strong>
          </p>
          {!anyBridgeOk && detectedPlatform === 'web' && (
            <p className="text-xs mt-2 text-amber-600">
              ⚠️ Se stai aprendo questa pagina <em>dentro l'app</em> e vedi questo
              messaggio, significa che il wrapper (Capacitor o Natively) non sta
              iniettando il bridge JavaScript. In questa modalità i pagamenti
              andranno via Stripe (web), NON via Apple IAP / Google Play.
            </p>
          )}
        </section>

        {/* Capacitor row ------------------------------------------- */}
        <DiagRow
          title="Capacitor"
          ok={capacitorOk}
          rows={[
            ['window.Capacitor presente', String(signals.capacitor.present)],
            ['getPlatform()', String(signals.capacitor.platform)],
            ['isNativePlatform()', String(signals.capacitor.isNative)],
            [
              'Plugin FirebaseMessaging bundled',
              signals.capacitor.firebaseMessagingPresent ? '✅ sì' : '❌ NO',
            ],
            [
              'Plugin PushNotifications bundled',
              signals.capacitor.pushNotificationsPresent ? 'sì (legacy)' : 'no',
            ],
            [
              'Tutti i plugin disponibili',
              (signals.capacitor.pluginNames || []).join(', ') || '(nessuno)',
            ],
          ]}
          isDark={isDark}
        />

        {/* Interactive push test ----------------------------------- */}
        {capacitorOk && (
          <section
            className={`rounded-2xl p-5 ${
              isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'
            }`}
            data-testid="diag-push-test"
          >
            <div className="flex items-center justify-between mb-3 gap-2">
              <h3 className="font-bold text-base">🔔 Test FCM Push</h3>
              <div className="flex items-center gap-2">
                {pushLog.length > 0 && (
                  <button
                    onClick={() => {
                      const text = pushLog
                        .map((e) => `[${e.status}] ${e.step}  (${e.ts})${e.detail ? '\n  ' + e.detail : ''}`)
                        .join('\n\n');
                      try {
                        navigator.clipboard.writeText(text);
                        setPushLogCopied(true);
                        setTimeout(() => setPushLogCopied(false), 2000);
                      } catch {
                        // Fallback for older WKWebView: select & copy via execCommand
                        const ta = document.createElement('textarea');
                        ta.value = text;
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                        setPushLogCopied(true);
                        setTimeout(() => setPushLogCopied(false), 2000);
                      }
                    }}
                    className={`px-3 py-2 rounded-full text-xs font-medium ${
                      isDark ? 'bg-white/10 text-white active:bg-white/20' : 'bg-gray-200 text-gray-800 active:bg-gray-300'
                    }`}
                    data-testid="diag-copy-push-log-btn"
                  >
                    {pushLogCopied ? '✓ Copiato' : 'Copia log'}
                  </button>
                )}
                <button
                  onClick={runPushTest}
                  disabled={pushRunning}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    pushRunning
                      ? 'bg-gray-400 text-white'
                      : 'bg-red-500 text-white active:bg-red-600'
                  }`}
                  data-testid="diag-run-push-test-btn"
                >
                  {pushRunning ? 'Test in corso…' : 'Avvia test'}
                </button>
              </div>
            </div>
            <p className={`text-xs mb-3 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              Esegue tutto il flusso: permesso → APNs token → FCM token → save backend → push test.
              Mostra ogni step in chiaro qui sotto. Se sei admin, alla fine parte anche una push test.
            </p>
            {/* Big "Copy FCM Token" button — for pasting in Firebase Console
                manual notification test ("Verifica con dispositivo"). */}
            {lastFcmToken && (
              <button
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(lastFcmToken);
                    setFcmTokenCopied(true);
                    setTimeout(() => setFcmTokenCopied(false), 2500);
                  } catch {
                    const ta = document.createElement('textarea');
                    ta.value = lastFcmToken;
                    document.body.appendChild(ta);
                    ta.select();
                    document.execCommand('copy');
                    document.body.removeChild(ta);
                    setFcmTokenCopied(true);
                    setTimeout(() => setFcmTokenCopied(false), 2500);
                  }
                }}
                className={`mb-3 w-full px-4 py-3 rounded-xl text-sm font-semibold ${
                  fcmTokenCopied
                    ? 'bg-green-500 text-white'
                    : isDark
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40 active:bg-blue-500/30'
                    : 'bg-blue-50 text-blue-700 border border-blue-200 active:bg-blue-100'
                }`}
                data-testid="diag-copy-fcm-token-btn"
              >
                {fcmTokenCopied ? '✓ Token FCM copiato' : '📋 Copia FCM Token (per Firebase Console)'}
              </button>
            )}
            {pushLog.length > 0 && (
              <div className={`rounded-lg overflow-hidden text-xs font-mono ${
                isDark ? 'bg-black/40' : 'bg-white border border-gray-200'
              }`}>
                {pushLog.map((entry, i) => {
                  const color = entry.status === 'OK'
                    ? 'text-green-500'
                    : entry.status === 'FAIL'
                    ? 'text-red-500'
                    : entry.status === 'WAIT'
                    ? 'text-amber-500'
                    : isDark ? 'text-white/70' : 'text-gray-600';
                  return (
                    <div key={i} className={`px-3 py-2 border-b ${
                      isDark ? 'border-white/10' : 'border-gray-100'
                    }`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`font-bold ${color}`}>
                          [{entry.status}] {entry.step}
                        </span>
                        <span className={`text-[10px] opacity-50 shrink-0`}>{entry.ts}</span>
                      </div>
                      {entry.detail && (
                        <div className={`mt-1 break-all ${isDark ? 'text-white/60' : 'text-gray-700'}`}>
                          {entry.detail}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* HealthKit / Health Connect diagnostic — shows whether the
            @capgo/capacitor-health plugin is wired and which read/write
            permissions the user actually granted. The most useful
            signal when "sync says 0 passi" but the system Health app
            has thousands of steps for today. */}
        <HealthKitDiagnostic isDark={isDark} />

        {/* Natively row (hidden when we're in a real Capacitor build —
            in that case window.natively is an unrelated leftover and
            its "Assente" badge confuses the diagnostic). */}
        {!capacitorOk && (
          <DiagRow
            title="Natively"
            ok={nativelyOk}
            rows={[
              ['window.natively presente', String(signals.natively.present)],
              ['platform (explicit)', String(signals.natively.platform)],
              ['isWebview', String(signals.natively.isWebview)],
              ['UA contiene "Natively/iOS"', String(ua.includes('natively/ios'))],
              ['UA contiene "Natively/Android"', String(ua.includes('natively/android'))],
            ]}
            isDark={isDark}
          />
        )}

        {/* NativelyPurchases SDK (same logic — only show if NOT Capacitor) */}
        {!capacitorOk && (
          <>
            <DiagRow
              title="SDK Acquisti In-App (NativelyPurchases)"
              ok={signals.nativelyPurchases.present}
              rows={[
                ['window.NativelyPurchases presente', String(signals.nativelyPurchases.present)],
                ['è una function/class', String(signals.nativelyPurchases.isFunction)],
              ]}
              isDark={isDark}
            />
            {!signals.nativelyPurchases.present && anyBridgeOk && (
              <div className={`text-xs p-3 rounded-lg ${
                isDark ? 'bg-amber-500/10 text-amber-300 border border-amber-500/30' : 'bg-amber-50 text-amber-800 border border-amber-200'
              }`}>
                ⚠️ Sei dentro l'app Natively ma l'SDK NativelyPurchases non è
                iniettato. Vai sulla dashboard buildnatively.com → progetto Slimmy
                → Features → In-App Purchases, abilitalo, incolla le RevenueCat
                Public API Keys (iOS appl_... + Android goog_...), salva e
                rigenera la build TestFlight/Play Internal.
              </div>
            )}

            <DiagRow
              title="SDK Push (NativelyFirebaseNotifications)"
              ok={signals.nativelyPush.present}
              rows={[
                ['window.NativelyFirebaseNotifications presente', String(signals.nativelyPush.present)],
                ['è una function/class', String(signals.nativelyPush.isFunction)],
              ]}
              isDark={isDark}
            />
            {!signals.nativelyPush.present && anyBridgeOk && (
              <div className={`text-xs p-3 rounded-lg ${
                isDark ? 'bg-amber-500/10 text-amber-300 border border-amber-500/30' : 'bg-amber-50 text-amber-800 border border-amber-200'
              }`}>
                ⚠️ Sei dentro l'app Natively ma l'SDK delle notifiche Firebase non
                è iniettato — per questo le push non arrivano su TestFlight. Vai
                su buildnatively.com → progetto Slimmy → Features → Push
                Notifications (Firebase), abilitalo, carica il file
                `firebase-admin.json` (lato Service Account) + la chiave APNs
                `.p8` (Apple Developer → Keys), salva e rigenera la build.
              </div>
            )}
          </>
        )}

        {/* Browser row --------------------------------------------- */}
        <DiagRow
          title="Browser / UA (informativo)"
          ok={null}
          rows={[
            ['userAgent', signals.browser.userAgent],
            ['navigator.platform', signals.browser.platform],
            ['standalone (iOS PWA)', String(signals.browser.standalone)],
            ['display-mode standalone', String(signals.browser.displayMode)],
            [
              'viewport',
              `${signals.window.width}×${signals.window.height} @${signals.window.devicePixelRatio}x`,
            ],
          ]}
          isDark={isDark}
        />

        {/* Payments config snapshot -------------------------------- */}
        <DiagRow
          title="Configurazione pagamenti (lato server)"
          ok={null}
          rows={[
            ['Stripe abilitato', String(paymentsConfig?.providers?.stripe)],
            ['Apple IAP abilitato', String(paymentsConfig?.providers?.apple_iap)],
            ['Google Play abilitato', String(paymentsConfig?.providers?.google_play)],
            ['iOS credenziali pronte', String(paymentsConfig?.ios?.configured)],
            ['Android credenziali pronte', String(paymentsConfig?.android?.configured)],
          ]}
          isDark={isDark}
        />

        {/* Copy payload -------------------------------------------- */}
        <button
          onClick={copyAll}
          data-testid="diag-copy-btn"
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium ${
            copied
              ? 'bg-green-500 text-white'
              : 'bg-[#E8334A] text-white'
          }`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copiato' : 'Copia diagnostica'}
        </button>

        <p className={`text-xs text-center ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
          Tocca "Copia" e incolla il risultato in chat per analisi rapida.
        </p>

        {/* GPS Background diagnostic — live tracker to verify that
            background location keeps working with screen locked. */}
        <GpsBackgroundDiagnostic />

        {/* RevenueCat / App Store price diagnostic — verifies the
            subscription currency returned by StoreKit on this device
            matches the user's storefront, with reference table for the
            10 primary target countries. */}
        <RevenueCatPriceDiagnostic isDark={isDark} />
      </div>
    </div>
  );
}

function DiagRow({ title, ok, rows, isDark }) {
  return (
    <section
      className={`rounded-2xl p-4 ${
        isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-semibold text-sm flex-1">{title}</h3>
        {ok === true && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-500 font-semibold">
            OK
          </span>
        )}
        {ok === false && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 font-semibold">
            Assente
          </span>
        )}
      </div>
      <dl className="space-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[1fr_2fr] gap-2 text-xs">
            <dt className={isDark ? 'text-white/50' : 'text-gray-500'}>{k}</dt>
            <dd
              className={`font-mono break-words ${
                isDark ? 'text-white/90' : 'text-gray-900'
              }`}
            >
              {String(v) || '—'}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}


/**
 * HealthKit / Health Connect inline diagnostic.
 *
 * Calls the @capgo/capacitor-health plugin directly (bypassing the
 * Slimmy wrapper) so we surface the raw native response.
 *
 * The most common failure mode this catches: the user accepted the
 * Apple Health auth dialog but iOS shows write and read permissions
 * on two separate screens, and the user only ticked write. In that
 * case `checkAuth.readDenied` lists every data type — pointing the
 * user to "Salute → Profilo → App → Slimmy" to fix it.
 */
function HealthKitDiagnostic({ isDark }) {
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);

  const run = async () => {
    setRunning(true);
    const out = {
      isNative: false,
      platform: null,
      ts: new Date().toISOString(),
    };
    try {
      const { Capacitor } = await import('@capacitor/core');
      out.isNative = Capacitor.isNativePlatform();
      out.platform = Capacitor.getPlatform();
    } catch (e) {
      out.capacitorError = String(e?.message || e);
    }
    try {
      const { Health } = await import('@capgo/capacitor-health');
      try {
        out.pluginVersion = await Health.getPluginVersion();
      } catch (e) {
        out.pluginVersionError = String(e?.message || e);
      }
      try {
        out.isAvailable = await Health.isAvailable();
      } catch (e) {
        out.isAvailableError = String(e?.message || e);
      }
      try {
        out.checkAuth = await Health.checkAuthorization({
          read: ['steps', 'distance', 'calories', 'heartRate', 'weight'],
          write: ['weight'],
        });
      } catch (e) {
        out.checkAuthError = String(e?.message || e);
      }
      // Quick smoke-read for today's steps so the user sees the
      // actual count the bridge returns (vs. what Apple Health shows).
      try {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const res = await Health.readSamples({
          dataType: 'steps',
          startDate: startOfDay.toISOString(),
          endDate: now.toISOString(),
          limit: 2000,
          ascending: true,
        });
        const totalSteps = (res?.samples || []).reduce(
          (acc, s) => acc + Math.round(s.value || 0), 0
        );
        out.stepsToday = {
          sampleCount: res?.samples?.length || 0,
          totalSteps,
          firstSample: res?.samples?.[0] || null,
        };
      } catch (e) {
        out.stepsTodayError = String(e?.message || e);
      }
    } catch (e) {
      out.pluginImportError = String(e?.message || e);
    }
    setReport(out);
    setRunning(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
      alert('Diagnostica HealthKit copiata');
    } catch {
      alert(JSON.stringify(report, null, 2));
    }
  };

  return (
    <section
      className={`mt-6 rounded-2xl p-4 ${
        isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'
      }`}
      data-testid="diag-healthkit-section"
    >
      <div className="flex items-center justify-between mb-3 gap-3">
        <div>
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Apple Health / Health Connect
          </h3>
          <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            Verifica plugin nativo, permessi e lettura passi di oggi.
          </p>
        </div>
        <button
          onClick={run}
          disabled={running}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0 ${
            running
              ? isDark ? 'bg-white/10 text-white/40' : 'bg-gray-200 text-gray-400'
              : isDark ? 'bg-[#E8334A] text-white' : 'bg-[#E8334A] text-white'
          }`}
          data-testid="diag-healthkit-run-btn"
        >
          {running ? 'In corso…' : 'Esegui'}
        </button>
      </div>

      {report && (
        <>
          <button
            onClick={copy}
            className={`mb-2 text-[11px] underline ${isDark ? 'text-white/70' : 'text-gray-600'}`}
            data-testid="diag-healthkit-copy-btn"
          >
            Copia JSON
          </button>
          <pre
            className={`text-[10px] leading-tight whitespace-pre-wrap break-all p-3 rounded-lg max-h-96 overflow-auto ${
              isDark ? 'bg-black/40 text-white/80' : 'bg-white text-gray-800 border border-gray-200'
            }`}
            data-testid="diag-healthkit-output"
          >
{JSON.stringify(report, null, 2)}
          </pre>
        </>
      )}
    </section>
  );
}

